import { useListaDeGmuds } from '../../../api/queries/gmuds'
import { temPermissao, useSessao } from '../../../auth/contexto'
import { Botao, BotaoLink } from '../../../components/Botao'
import { AvisoDeErro, Esqueleto, EstadoVazio } from '../../../components/Estados'
import { IconeBusca, IconeMudanca, IconeNovo } from '../../../components/Icones'
import { Paginacao } from '../../../components/Paginacao'
import { TERMOS, TEXTOS } from '../../../lib/glossario'
import { ChipsDeTriagem } from './ChipsDeTriagem'
import { FiltrosDaLista } from './FiltrosDaLista'
import { ListagemDeGmuds } from './ListagemDeGmuds'
import { AvisoDeHistorico, LinhaDoRecorte } from './marcadores'
import { useFiltrosNaUrl } from './filtros'
import { useLembrarBusca } from '../../../lib/ultimaBusca'

/**
 * A tela onde o trabalho começa: todas as solicitações, filtráveis e com link compartilhável.
 *
 * O botão de abrir solicitação só aparece para quem o backend disse que pode
 * (`permissoes` de `GET /me`). Isso é conveniência de menu, não autorização: quem
 * forçar a rota `/gmuds/novo` recebe a recusa do servidor no envio, que é onde a regra
 * mora de verdade.
 *
 * @returns a listagem
 */
export function PaginaListaGmuds() {
  const { filtros, alterar, limpar, temFiltro } = useFiltrosNaUrl()
  useLembrarBusca('/gmuds')
  const { data: pagina, isPending, isError, error } = useListaDeGmuds(filtros)
  const { usuario } = useSessao()
  const podeCriar = temPermissao(usuario, 'GMUD_CRIAR')

  // Só quando o recorte é EXCLUSIVAMENTE o histórico. Com `V2` também marcado a lista é mista,
  // e um aviso dizendo "isto é histórico" sobre uma tela que tem registro do processo novo
  // seria pior do que não avisar nada — cada linha já se identifica sozinha.
  //
  // É outra coisa da `LinhaDoRecorte` abaixo do título, que aparece sempre: a linha diz QUAL
  // recorte está em vigor e como trocar; este aviso explica o que aqueles registros são.
  const soHistorico = filtros.origem.length === 1 && filtros.origem[0] === 'MIGRADO_V1'

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="titulo-da-pagina">{TERMOS.gmud.rotuloPlural}</h1>
          <p className="mt-1 max-w-prose text-sm text-slate-600">{TERMOS.gmud.ajuda}</p>
          <div className="mt-1 max-w-prose">
            <LinhaDoRecorte origem={filtros.origem} />
          </div>
        </div>
        {podeCriar && (
          <BotaoLink to="/gmuds/novo" aparencia="primario">
            <IconeNovo className="h-4 w-4" />
            {TEXTOS.abrirSolicitacao}
          </BotaoLink>
        )}
      </header>

      <ChipsDeTriagem filtros={filtros} alterar={alterar} />
      <FiltrosDaLista filtros={filtros} alterar={alterar} limpar={limpar} temFiltro={temFiltro} />

      {soHistorico && <AvisoDeHistorico />}

      {isPending && (
        <div className="cartao px-4 py-5">
          <Esqueleto linhas={9} />
        </div>
      )}
      {isError && <AvisoDeErro erro={error} titulo="Não foi possível carregar a lista" />}

      {pagina && pagina.conteudo.length === 0 && (
        <EstadoVazio
          icone={temFiltro ? <IconeBusca className="h-9 w-9" /> : <IconeMudanca className="h-9 w-9" />}
          titulo={temFiltro ? TEXTOS.semResultados.titulo : TEXTOS.listaVazia.titulo}
          ajuda={temFiltro ? TEXTOS.semResultados.ajuda : TEXTOS.listaVazia.ajuda}
          acao={
            temFiltro ? (
              <Botao onClick={limpar}>{TEXTOS.limparFiltros}</Botao>
            ) : (
              podeCriar && (
                <BotaoLink to="/gmuds/novo" aparencia="primario">
                  <IconeNovo className="h-4 w-4" />
                  {TEXTOS.abrirSolicitacao}
                </BotaoLink>
              )
            )
          }
        />
      )}

      {pagina && pagina.conteudo.length > 0 && (
        <>
          <ListagemDeGmuds itens={pagina.conteudo} />
          <Paginacao
            pagina={filtros.pagina}
            totalPaginas={pagina.totalPaginas}
            totalElementos={pagina.totalElementos}
            aoMudar={(numero) => alterar({ pagina: numero })}
          />
        </>
      )}
    </div>
  )
}
